/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, December 23rd 2021, 10:07:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { OSAvatar } from "ui-widgets";
import Skeleton from "@mui/material/Skeleton";
import { PreBuiltService } from "lib/schemas";
import { iso8601DateToDateString } from "lib/utils";
import { SuccessCheckButton } from "ui-widgets";
import { truncate } from "lodash";
import Button from "@mui/material/Button";

export interface PreBuiltServiceCardProps {
  prebuilt_service: PreBuiltService;
  onSelectTemplate: (prebuilt_service?: PreBuiltService) => void; // Passing prebuilt_service so that onSelectTemplate can be used in any lifted state above the grid.
}

export const PreBuiltServiceCard = ({ prebuilt_service, onSelectTemplate }: PreBuiltServiceCardProps) => {
  const title = prebuilt_service.title;

  const main_image = prebuilt_service.cover_art;

  const description = prebuilt_service.description;

  const date_published = iso8601DateToDateString(prebuilt_service.date_published);

  const handleViewPreBuilt = () => {
    onSelectTemplate?.(prebuilt_service);
  };

  return (
    <div className="os-service-card-outer-wrapper" onClick={handleViewPreBuilt}>
      <Card className="os-service-card-wrapper" sx={{ maxWidth: 345 }}>
        <CardMedia
          className="os-service-card-media"
          component="img"
          height="194"
          image={main_image}
          alt="Card Image"
          onClick={handleViewPreBuilt}
          sx={{ cursor: "pointer" }}
        />
        <CardHeader
          className="os-service-card-header"
          avatar={<OSAvatar name={title} splitFirstOnly={false} />}
          title={truncate(title)}
          subheader={date_published}
        />

        {description && (
          <CardContent className="os-service-card-summary">
            <div className="os-service-card-summary-text">
              <Typography variant="body2" color="text.secondary">
                {truncate(description, { length: 60, omission: ".." })}
              </Typography>
            </div>
            <div className="os-service-card-summary-action d-flex">
              <Button variant="outlined">Preview</Button>
              <div className="os-service-card-summary-action-status">
                {prebuilt_service.is_installed ? <SuccessCheckButton> Installed</SuccessCheckButton> : null}
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export const PreBuiltServiceCardSkeleton = () => {
  return (
    <Card sx={{ maxWidth: 345 }}>
      <CardMedia>
        <Skeleton animation="wave" variant="rectangular" height={194} />
      </CardMedia>
      <CardHeader
        avatar={<Skeleton variant="circular" width={40} height={40} />}
        title={<Skeleton animation="wave" height={10} width="80%" style={{ marginBottom: 6 }} />}
        subheader={<Skeleton animation="wave" height={10} width="40%" />}
      />
      <Skeleton animation="wave" height={10} width="80%" style={{ marginLeft: 16, marginBottom: 6 }} />
      <Skeleton animation="wave" height={30} width={60} style={{ marginLeft: 16, marginBottom: 12 }} />
    </Card>
  );
};
